import { __decorate } from "tslib";
import { ResizeSensor } from 'css-element-queries';
import { VIDEO_EVENTS, UI_EVENTS, EngineState as STATES, } from 'playable/dist/src/constants';
import playerAPI from 'playable/dist/src/core/player-api-decorator';
import { noop } from 'lodash';
import CardsContainer from './cards-container/cards-container';
import CardsManager from './cards-manager/cards-manager';
import CardsConfig from './cards-config/cards-config';
import { EVENTS } from './constants';
import { waitForDomUpdate } from './utils/dom-update-delay';
export class CardsModule {
    constructor({ eventEmitter, rootContainer, engine }) {
        this.eventEmitter = eventEmitter;
        this.rootContainer = rootContainer;
        this.engine = engine;
        this.cardsConfig = new CardsConfig();
        this.isInitialized = false;
        this.hide = noop;
        this.show = noop;
        this.handlePlayerSizeChange = this.handlePlayerSizeChange.bind(this);
    }
    // Lazy initialization of cards-related stuff
    initialize() {
        if (this.isInitialized) {
            return;
        }
        this.initContainer();
        this.initManager();
        this.initEventListeners();
        this.hide = () => this.cardsContainer.hide();
        this.show = () => this.cardsContainer.show();
        this.isInitialized = true;
        setTimeout(() => this.handlePlayerStateChange({
            nextState: this.engine.getCurrentState(),
        }), 0);
    }
    initContainer() {
        this.cardsContainer = new CardsContainer({ cardsConfig: this.cardsConfig });
        this.rootContainer.appendComponentElement(this.cardsContainer.node);
    }
    initManager() {
        this.cardsManager = new CardsManager({
            cardsContainer: this.cardsContainer,
            eventEmitter: this.eventEmitter,
            cardsConfig: this.cardsConfig,
            engine: this.engine,
        });
    }
    initEventListeners() {
        this.eventEmitter.on(VIDEO_EVENTS.STATE_CHANGED, this.handlePlayerStateChange, this);
        this.eventEmitter.on(UI_EVENTS.MAIN_BLOCK_HIDE, this.cardsContainer.onControlsHidden, this.cardsContainer);
        this.eventEmitter.on(UI_EVENTS.MAIN_BLOCK_SHOW, this.cardsContainer.onControlsShown, this.cardsContainer);
        // wait for playerContainerNode inserted to DOM and get size for case if it's not added yet
        waitForDomUpdate().then(() => (this.resizeSensor = new ResizeSensor(this.rootContainer.getElement(), this.handlePlayerSizeChange)));
        this.eventEmitter.on(EVENTS.CARD_CLOSED, this.cardsManager.handleCardClose, this.cardsManager);
    }
    addCard(cardData) {
        this.initialize();
        return this.cardsManager.addCard(cardData);
    }
    addCards(cardsData) {
        this.initialize();
        return this.cardsManager.addCards(cardsData);
    }
    removeCard(id) {
        this.cardsManager.removeCard(id);
    }
    updateCards(cardsData) {
        return this.cardsManager.updateCards(cardsData);
    }
    clearCards() {
        if (!this.isInitialized) {
            return;
        }
        this.cardsManager.clearCards();
    }
    setAnchorPoint(anchorPoint) {
        if (this.cardsConfig.anchorPoint === anchorPoint) {
            return;
        }
        this.cardsConfig.anchorPoint = anchorPoint;
    }
    setOrientation(orientation) {
        if (this.cardsConfig.orientation === orientation) {
            return;
        }
        this.cardsConfig.orientation = orientation;
    }
    onCardClose(callback) {
        this.eventEmitter.on(EVENTS.CARD_CLOSED, callback);
    }
    onCardShown(callback) {
        this.eventEmitter.on(EVENTS.CARD_SHOWN, callback);
    }
    onCardHidden(callback) {
        this.eventEmitter.on(EVENTS.CARD_HIDDEN, callback);
    }
    setCardsClosable(isClosable) {
        const isPreviewMode = !isClosable;
        if (this.cardsConfig.isPreviewMode === isPreviewMode) {
            return;
        }
        this.cardsConfig.isPreviewMode = isPreviewMode;
    }
    setActiveCard(id) {
        if (this.isInitialized) {
            return this.cardsManager.showSelectedCard(id);
        }
    }
    hideCardsContainer() {
        this.hide();
    }
    showCardsContainer() {
        this.show();
    }
    handlePlayerSizeChange() {
        this.cardsManager.handlePlayerSizeChange();
    }
    handlePlayerStateChange({ nextState }) {
        switch (nextState) {
            case STATES.PLAYING:
                this.cardsManager.handleVideoPlayStart();
                break;
            case STATES.PAUSED:
                this.cardsManager.handleVideoPlayPause();
                break;
            case STATES.SEEK_IN_PROGRESS:
                this.cardsManager.handleSeekPositionChange();
                break;
            default:
                break;
        }
    }
    unbindEvents() {
        this.eventEmitter.off(VIDEO_EVENTS.STATE_CHANGED, this.handlePlayerStateChange, this);
        this.eventEmitter.off(UI_EVENTS.MAIN_BLOCK_HIDE, this.cardsContainer.onControlsHidden, this.cardsContainer);
        this.eventEmitter.off(UI_EVENTS.MAIN_BLOCK_SHOW, this.cardsContainer.onControlsShown, this.cardsContainer);
        this.eventEmitter.off(EVENTS.CARD_CLOSED);
        this.eventEmitter.off(EVENTS.CARD_SHOWN);
        this.eventEmitter.off(EVENTS.CARD_HIDDEN);
    }
    destroy() {
        if (!this.isInitialized) {
            return;
        }
        this.unbindEvents();
        this.cardsContainer.destroy();
        this.cardsManager.destroy();
        this.cardsConfig.destroy();
        if (this.resizeSensor) {
            this.resizeSensor.detach(this.handlePlayerSizeChange);
        }
        delete this.cardsContainer;
        delete this.cardsManager;
        delete this.cardsConfig;
    }
}
CardsModule.dependencies = ['eventEmitter', 'rootContainer', 'engine'];
__decorate([
    playerAPI()
], CardsModule.prototype, "addCard", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "addCards", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "removeCard", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "updateCards", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "clearCards", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "setAnchorPoint", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "setOrientation", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "onCardClose", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "onCardShown", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "onCardHidden", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "setCardsClosable", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "setActiveCard", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "hideCardsContainer", null);
__decorate([
    playerAPI()
], CardsModule.prototype, "showCardsContainer", null);
//# sourceMappingURL=cards-module.js.map